/**
 * RUNS OF BUBBLES, AND WHERE A FACE GOES (wave 90).
 *
 * "The icons of the nob / the user near every bubble, like Facebook
 * Messenger." A thread groups consecutive bubbles from one party into a RUN,
 * and the run wears its speaker's face once — beside its LAST bubble, at the
 * bottom-left — while its small label (a name, "to you") stands once above its
 * FIRST. The reader's own bubbles are never faced.
 *
 * Two rows of one party are one run when nobody else spoke between them and
 * they are within `RUN_GAP_MS` of each other. A line with no party — the room's
 * own notices, "Asked them: …", a work line, a card — belongs to no run and
 * breaks the one it interrupts.
 *
 * ONE FUNCTION FOR EVERY THREAD, so the owner's chat with their Nob, the
 * owner's view of a knock and the knocker's own thread cannot come to three
 * different answers about the same two lines — and so a reload, which reads
 * the same rows with the server's clock, groups them exactly as the live
 * render did with the browser's.
 */

/** Five minutes: past this, the same speaker starts a new run. */
export const RUN_GAP_MS = 5 * 60 * 1000;

/** One row as a run sees it: who spoke (null for no one), and when (null when unknown). */
export type RunItem = { party: string | null; at: number | null };

export type RunPosition = {
  /** The first bubble of its run: the label goes above this one. */
  first: boolean;
  /** The last bubble of its run: the face goes beside this one. */
  last: boolean;
};

/** A timestamp as a run reads it: milliseconds, or null for a row without one. */
export function runAt(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function joins(a: RunItem | undefined, b: RunItem | undefined): boolean {
  if (!a || !b || a.party === null || a.party !== b.party) return false;
  // A row with no moment cannot be shown to be far from its neighbour.
  if (a.at === null || b.at === null) return true;
  return Math.abs(b.at - a.at) <= RUN_GAP_MS;
}

/** Where each row stands in its run. A row with no party is its own first and last. */
export function runPositions(items: readonly RunItem[]): RunPosition[] {
  return items.map((item, i) => ({
    first: !joins(items[i - 1], item),
    last: !joins(item, items[i + 1]),
  }));
}
